import { createColumnHelper } from '@tanstack/react-table';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BadgeText, Button, IconSvg, Text, Tooltip } from '@/lib/v2/components';
import { ViewsIcon as ViewsIconSolid } from '@/lib/v2/icons/solid';

import { ActionRowMenu } from '@/src/modules/AutomationModuleNew/components';
import { AUTOMATION_PATHS } from '@/src/modules/AutomationModuleNew/constants';
import { useHandleRowActions } from '@/src/modules/AutomationModuleNew/hooks/useHandleRowActions';
import { RulesIcon } from '@/src/modules/AutomationModuleNew/icons';
import {
  EJourneyMessageState,
  JourneysMessagesTable,
} from '@/src/modules/AutomationModuleNew/interfaces/automation';

import { DateFormat, formatDate } from '@/modules/CampaignsModule/utils';

export const useJourneysMessagesTableColumns = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<JourneysMessagesTable>();
  const { data: actionRowMenuData, handleRowMenuClick } =
    useHandleRowActions<JourneysMessagesTable>();

  const statusTranslationMap = useMemo<Record<EJourneyMessageState, string>>(
    () => ({
      [EJourneyMessageState.ACTIVE]: 'useJourneysMessageFilters.filter.active',
      [EJourneyMessageState.INACTIVE]: 'useJourneysMessageFilters.filter.inactive',
      [EJourneyMessageState.DRAFT]: 'useJourneysMessageFilters.filter.draft',
    }),
    []
  );

  const handleClickPreview = useCallback(
    (event: MouseEvent<HTMLButtonElement>, value: JourneysMessagesTable) => {
      event.stopPropagation();

      const { campaignId, id: actionId } = value;

      navigate(
        `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${String(
          campaignId
        )}/action/${actionId}/email-trigger/preview`
      );
    },
    [navigate]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('useJourneysMessagesTableColumns.tableHeader.name') as unknown as string,
        cell: (info) => {
          const isActive = info.row.original.status === EJourneyMessageState.ACTIVE;
          return (
            <div className="flex w-full flex-row items-center gap-4">
              <IconSvg
                rounded
                bgColor={isActive ? 'success_new' : 'inactive'}
                fillColor={isActive ? 'success_new' : 'inactive'}
                height="auto"
                svgComponent={<RulesIcon />}
                width="40px"
                withPadding="small"
              />
              <div className="flex-1">
                <Tooltip
                  isClickable
                  noArrow
                  alignment="start"
                  content={info.getValue()}
                  position="top"
                >
                  <Text fontWeight="medium" variant="text">
                    {info.getValue()}
                  </Text>
                </Tooltip>
              </div>
            </div>
          );
        },
        meta: {
          widthClassName: 'w-[40%]',
          enableOrderColumn: true,
        },
      }),
      columnHelper.accessor('status', {
        header: t('useJourneysMessagesTableColumns.tableHeader.status') as unknown as string,
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          return (
            <BadgeText
              text={t(statusTranslationMap[info.row.original.status])}
              variantColor={
                info.row.original.status === EJourneyMessageState.ACTIVE ? 'success' : 'inactive'
              }
            />
          );
        },
      }),
      columnHelper.accessor('creationDate', {
        header: t('useJourneysMessagesTableColumns.tableHeader.creationDate') as unknown as string,
        meta: {
          widthClassName: 'w-[20%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatDate(info.getValue(), DateFormat.LongDateOutSeconds)
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.accessor('lastUpdate', {
        header: t('useJourneysMessagesTableColumns.tableHeader.lastUpdate') as unknown as string,
        meta: {
          widthClassName: 'w-[20%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatDate(info.getValue(), DateFormat.LongDateOutSeconds)
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.display({
        id: 'action-buttons',
        header: '',
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          return (
            <div className="flex items-center gap-2">
              <Button
                gray
                onlyIcon
                outline
                id="preview-actions"
                size="small"
                title={'preview action'}
                onClick={(event) => handleClickPreview(event, info.row.original)}
              >
                <IconSvg fillColor="gray-dark" svgComponent={<ViewsIconSolid />} />
              </Button>
              <ActionRowMenu
                hideItems={['move']}
                id="options-actions-dropdown"
                onClick={handleRowMenuClick(info.row.original)}
              />
            </div>
          );
        },
      }),
    ];
  }, [columnHelper, handleClickPreview, handleRowMenuClick, statusTranslationMap, t]);

  return { columns, actionRowMenuData: actionRowMenuData ? [actionRowMenuData] : undefined };
};
